/**
 * Máscaras de input pt-BR usadas em CasosPage (filtros) e NovoCasoPage
 * (formulário). Funções puras — sem side effects, sem React.
 * Convenção: aceitam string parcial (digitação em curso) e devolvem
 * a string formatada até onde der; nunca cortam dígitos válidos.
 */

const CPF_LEN = 11;
const CNJ_LEN = 20;
const BYTE_LABELS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
const BYTE_BASE = 1024;

/** Retorna apenas os dígitos da entrada. */
export function digitsOnly(input: string): string {
  return input.replace(/\D/g, '');
}

/**
 * CPF com onze dígitos, não todos iguais, e os dois verificadores certos (E165.F1.T03, a mesma conta
 * do `cpf_valido` do servidor). Aceita com ou sem máscara.
 */
export function cpfValido(input: string): boolean {
  const d = digitsOnly(input);
  if (d.length !== CPF_LEN || new Set(d).size === 1) return false;
  const verificador = (base: string): number => {
    const soma = [...base].reduce((acc, c, i) => acc + Number(c) * (base.length + 1 - i), 0);
    const resto = soma % CPF_LEN;
    return resto < 2 ? 0 : CPF_LEN - resto;
  };
  return (
    verificador(d.slice(0, 9)) === Number(d[9]) && verificador(d.slice(0, 10)) === Number(d[10])
  );
}

/**
 * Formata CPF como 000.000.000-00 conforme o usuário digita.
 * Exemplos:
 *   "1234"             → "123.4"
 *   "12345678"         → "123.456.78"
 *   "12345678901"      → "123.456.789-01"
 *   "12345678901extra" → "123.456.789-01" (descarta excedente)
 */
export function formatCpf(input: string): string {
  const d = digitsOnly(input).slice(0, CPF_LEN);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * CPF mascarado como o exame o imprime (`***.090.689-**`), para mostrar a parte sem expor o número
 * inteiro na tela (E205.F1, cabeçalho da mesa). Sem onze dígitos, `null`.
 */
export function mascararCpf(input: string): string | null {
  const d = digitsOnly(input);
  if (d.length !== CPF_LEN) return null;
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
}

/**
 * Formata número CNJ como NNNNNNN-DD.AAAA.J.TR.OOOO (20 dígitos).
 * Aceita digitação parcial; preenche separadores apenas até onde der.
 */
export function formatCnj(input: string): string {
  const d = digitsOnly(input).slice(0, CNJ_LEN);
  if (d.length <= 7) return d;
  if (d.length <= 9) return `${d.slice(0, 7)}-${d.slice(7)}`;
  if (d.length <= 13) return `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9)}`;
  if (d.length <= 14) {
    return `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.${d.slice(13)}`;
  }
  if (d.length <= 16) {
    return `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.${d.slice(13, 14)}.${d.slice(14)}`;
  }
  return `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.${d.slice(13, 14)}.${d.slice(14, 16)}.${d.slice(16)}`;
}

/** Tamanho em bytes em forma humanizada pt-BR (vírgula decimal). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes === 0) return '0 B';
  let value = bytes;
  let unit = 0;
  while (value >= BYTE_BASE && unit < BYTE_LABELS.length - 1) {
    value /= BYTE_BASE;
    unit += 1;
  }
  const formatted = unit === 0 ? String(Math.round(value)) : value.toFixed(1).replace('.', ',');
  return `${formatted} ${BYTE_LABELS[unit]}`;
}
